import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const BalanceOverview = ({ selectedRail, onRailChange }) => {
  const [balances, setBalances] = useState({
    totalPending: 45280.50,
    availableBalance: 128450.75,
    inTransit: 8920.30,
    nextSettlement: 12450.00
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setBalances(prev => ({
        ...prev,
        totalPending: prev?.totalPending + (Math.random() - 0.5) * 100,
        availableBalance: prev?.availableBalance + (Math.random() - 0.5) * 200
      }));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const railOptions = [
    { value: 'all', label: 'All Payment Rails' },
    { value: 'bitcoin', label: 'Bitcoin' },
    { value: 'ethereum', label: 'Ethereum' },
    { value: 'stablecoin', label: 'Stablecoins' },
    { value: 'merchant', label: 'Merchant Fees' }
  ];

  const railBalances = [
    {
      id: 'bitcoin',
      name: 'Bitcoin',
      symbol: 'BTC',
      pending: 18450.32,
      available: 52340.50,
      icon: 'Bitcoin',
      color: '#F7931A',
      nextSettlement: '2 days'
    },
    {
      id: 'ethereum',
      name: 'Ethereum',
      symbol: 'ETH',
      pending: 12680.50,
      available: 38920.25,
      icon: 'Hexagon',
      color: '#627EEA',
      nextSettlement: '1 day'
    },
    {
      id: 'stablecoin',
      name: 'Stablecoins',
      symbol: 'USDC/USDT',
      pending: 8520.00,
      available: 24580.00,
      icon: 'DollarSign',
      color: '#10B981',
      nextSettlement: '3 days'
    },
    {
      id: 'merchant',
      name: 'Merchant Fees',
      symbol: 'USD',
      pending: 5629.68,
      available: 12610.00,
      icon: 'Store',
      color: '#8B5CF6',
      nextSettlement: '5 days'
    }
  ];

  return (
    <div className="mb-6">
      {/* Filter */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Balance Overview</h3>
        <div className="w-64">
          <Select
            options={railOptions}
            value={selectedRail}
            onChange={onRailChange}
            placeholder="Select payment rail"
          />
        </div>
      </div>

      {/* Main Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-warning/20 flex items-center justify-center">
              <Icon name="Clock" size={20} className="text-warning" />
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Icon name="Radio" size={12} className="text-success" />
              <span>Live</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-1">Pending Payouts</p>
          <h3 className="text-2xl font-bold text-foreground font-data">
            ${balances?.totalPending?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </h3>
          <p className="text-xs text-muted-foreground mt-2">Awaiting settlement</p>
        </div>

        <div className="card">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
              <Icon name="Wallet" size={20} className="text-success" />
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Icon name="Radio" size={12} className="text-success" />
              <span>Live</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-1">Available Balance</p>
          <h3 className="text-2xl font-bold text-foreground font-data">
            ${balances?.availableBalance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </h3>
          <p className="text-xs text-muted-foreground mt-2">Ready for withdrawal</p>
        </div>

        <div className="card">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Icon name="ArrowRightLeft" size={20} className="text-primary" />
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-1">In Transit</p>
          <h3 className="text-2xl font-bold text-foreground font-data">
            ${balances?.inTransit?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </h3>
          <p className="text-xs text-muted-foreground mt-2">Processing transfers</p>
        </div>

        <div className="card">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
              <Icon name="Calendar" size={20} className="text-accent" />
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-1">Next Settlement</p>
          <h3 className="text-2xl font-bold text-foreground font-data">
            ${balances?.nextSettlement?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </h3>
          <p className="text-xs text-muted-foreground mt-2">Scheduled in 24h</p>
        </div>
      </div>

      {/* Rail-Specific Balances */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {railBalances?.map(rail => (
          <div key={rail?.id} className="card hover:shadow-lg transition-smooth">
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${rail?.color}20` }}
              >
                <Icon name={rail?.icon} size={20} color={rail?.color} />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{rail?.name}</p>
                <p className="text-xs text-muted-foreground">{rail?.symbol}</p>
              </div>
            </div>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-muted-foreground">Pending</p>
                <p className="text-lg font-bold text-foreground font-data">
                  ${rail?.pending?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Available</p>
                <p className="text-sm font-semibold text-success font-data">
                  ${rail?.available?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="pt-2 border-t border-border flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Next settlement</span>
                <span className="text-xs font-semibold text-foreground">{rail?.nextSettlement}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BalanceOverview;